import { PLAN_DEFAULTS } from '@parcel/types';
import type { BillingPlan, BillingStatus } from '../../lib/billing.js';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function getBillingStatusLabel(status: BillingStatus): string {
  switch (status) {
    case 'active':
      return 'Active';
    case 'incomplete':
      return 'Incomplete';
    case 'incomplete_expired':
      return 'Incomplete expired';
    case 'past_due':
      return 'Past due';
    case 'canceled':
      return 'Canceled';
    case 'unpaid':
      return 'Unpaid';
  }
}

export function getRenewalText(
  currentPeriodEnd: string | null,
  cancelAtPeriodEnd: boolean,
): string | null {
  if (!currentPeriodEnd) return null;
  const date = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(currentPeriodEnd));
  return `${cancelAtPeriodEnd ? 'Cancels' : 'Renews'} ${date}`;
}

export function canShowBillingPortalButton(input: {
  stripeCustomerId: string | null;
  canManageBilling: boolean;
}): boolean {
  return Boolean(input.stripeCustomerId) && input.canManageBilling;
}

export function getPlanCardState(input: {
  plan: BillingPlan;
  currentPlan: BillingPlan;
  canManageBilling: boolean;
  hasStripeSubscription?: boolean;
}) {
  const isCurrent = input.plan === input.currentPlan;
  const hasStripeSubscription = Boolean(input.hasStripeSubscription);
  return {
    isCurrent,
    canStartCheckout:
      input.canManageBilling && !isCurrent && input.plan !== 'free' && !hasStripeSubscription,
    canOpenPortal: input.canManageBilling && !isCurrent && hasStripeSubscription,
  };
}

const PLAN_RANK: Record<BillingPlan, number> = { free: 0, starter: 1, pro: 2 };

/** Whether moving to `plan` from `currentPlan` is an upgrade. */
export function isPlanUpgrade(plan: BillingPlan, currentPlan: BillingPlan): boolean {
  return PLAN_RANK[plan] > PLAN_RANK[currentPlan];
}

/** The button in a plan's column on the Plans view. */
export function getPlanChangeLabel(input: {
  plan: BillingPlan;
  currentPlan: BillingPlan;
  canManageBilling: boolean;
  hasStripeSubscription?: boolean;
}): string {
  if (input.plan === input.currentPlan) return 'Current plan';
  if (!input.canManageBilling) return 'Ask an admin';
  return isPlanUpgrade(input.plan, input.currentPlan) ? 'Upgrade' : 'Downgrade';
}

/** One comparison cell: a check, a dash, or a figure. */
export type PlanComparisonValue = boolean | string;

export interface PlanComparisonRow {
  label: string;
  values: Record<BillingPlan, PlanComparisonValue>;
}

export interface PlanComparisonSection {
  title: string;
  rows: PlanComparisonRow[];
}

const COMPACT = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 });

function byPlan<T>(read: (plan: BillingPlan) => T): Record<BillingPlan, T> {
  return { free: read('free'), starter: read('starter'), pro: read('pro') };
}

/** The Plans view's comparison table, read from the enforced limits in PLAN_DEFAULTS. */
export function planComparison(options: { spec: boolean }): PlanComparisonSection[] {
  const limits = PLAN_DEFAULTS;
  return [
    {
      title: 'Usage',
      rows: [
        {
          label: 'Members',
          values: byPlan((plan) =>
            limits[plan].seats === 1 ? '1' : `Up to ${limits[plan].seats}`,
          ),
        },
        {
          label: 'Signal history',
          values: byPlan((plan) => {
            const days = limits[plan].signalHistoryDays;
            return days === null ? 'Full' : `${days} days`;
          }),
        },
        {
          label: 'Enrichment credits',
          values: byPlan((plan) => `${limits[plan].enrichCreditsMonthly} / month`),
        },
        ...(options.spec
          ? [
              {
                label: 'Parcel search',
                values: byPlan(
                  (plan) => `${COMPACT.format(limits[plan].specQueryRows24h)} rows / day`,
                ),
              },
              {
                label: 'Web search',
                values: byPlan((plan) => `${COMPACT.format(limits[plan].specWebCalls24h)} / day`),
              },
            ]
          : []),
      ],
    },
    {
      title: 'Features',
      rows: [
        { label: 'Real-time projects', values: byPlan(() => true) },
        { label: 'Scoring', values: byPlan(() => true) },
        // Free gets a counts-only daily email; the full digest is the paid feature.
        { label: 'Full daily email digest', values: byPlan((plan) => limits[plan].emailAlerts) },
        {
          label: 'Microsoft Teams & Slack alerts',
          values: byPlan((plan) => limits[plan].teamsAlerts),
        },
        { label: 'API access', values: byPlan((plan) => limits[plan].apiCreditCeiling24h > 0) },
        ...(options.spec ? [{ label: 'AI seats', values: byPlan((plan) => plan !== 'free') }] : []),
        // Extra workspace seats are sold on Pro only (`seatManagement.canManage`).
        { label: 'Additional seats', values: byPlan((plan) => plan === 'pro') },
      ],
    },
  ];
}

export function getBillingErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof TypeError && err.message === 'Failed to fetch') {
    return 'Unable to reach the local API. Start the API with Stripe sandbox env, then try again.';
  }

  if (isRecord(err) && isRecord(err.body) && isRecord(err.body.error)) {
    const error = err.body.error;
    if (error.code === 'stripe_misconfigured' && Array.isArray(error.missing)) {
      const missing = error.missing.filter((key): key is string => typeof key === 'string');
      if (missing.length > 0) {
        return `Stripe billing is missing required config: ${missing.join(', ')}.`;
      }
    }

    if (error.code === 'unauthorized') {
      return 'Checkout needs a real signed-in session. Sign in again or configure Supabase locally.';
    }

    if (typeof error.message === 'string') return error.message;
  }

  return err instanceof Error ? err.message : fallback;
}
